// src/utils/addressSplitting.ts
// Smart address splitting utilities for creating logical canvassing workloads

import { CreationAddress } from '../store/workloadStore';

interface SplitOptions {
  maxSize: number; // Maximum addresses per workload
  splitByStreet: boolean; // Group by street name
  splitByPolygon: boolean; // Keep each polygon as separate workload
  minSize?: number; // Minimum addresses per workload (default: 10)
}

interface WorkloadGroup {
  id: string;
  name: string;
  addresses: CreationAddress[];
  metadata: {
    streets: string[];
    polygonId?: string;
    bounds?: {
      north: number;
      south: number;
      east: number;
      west: number;
    };
  };
}

/**
 * Calculate the distance between two points in meters using Haversine formula
 */
function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Calculate bounds for a set of addresses
 */
function calculateBounds(addresses: CreationAddress[]) {
  if (addresses.length === 0) return undefined;

  let north = -Infinity;
  let south = Infinity;
  let east = -Infinity;
  let west = Infinity;

  for (const addr of addresses) {
    north = Math.max(north, addr.location.lat);
    south = Math.min(south, addr.location.lat);
    east = Math.max(east, addr.location.lng);
    west = Math.min(west, addr.location.lng);
  }

  return { north, south, east, west };
}

/**
 * Extract street name from full address (removes house number)
 */
function extractStreetName(street: string): string {
  return street.replace(/^\d+\s*/, '').trim();
}

/**
 * Sort addresses by geographic location for efficient walking routes
 * Uses a simple approach: sort by latitude, then within nearby latitudes, sort by longitude
 */
function sortAddressesForWalking(addresses: CreationAddress[]): CreationAddress[] {
  if (addresses.length === 0) return [];

  // First, sort by street name to keep same-street addresses together
  const sorted = [...addresses].sort((a, b) => {
    const streetA = a.streetName || extractStreetName(a.street);
    const streetB = b.streetName || extractStreetName(b.street);

    // Same street? Sort by house number
    if (streetA === streetB) {
      const numA = parseInt(a.street.match(/^\d+/)?.[0] || '0', 10);
      const numB = parseInt(b.street.match(/^\d+/)?.[0] || '0', 10);
      return numA - numB;
    }

    // Different streets: sort by geographic proximity
    // Use latitude as primary sort, then longitude
    const latDiff = a.location.lat - b.location.lat;
    if (Math.abs(latDiff) > 0.0001) {
      return latDiff;
    }
    return a.location.lng - b.location.lng;
  });

  return sorted;
}

/**
 * Group addresses by street name
 */
function groupByStreet(addresses: CreationAddress[]): Map<string, CreationAddress[]> {
  const streetGroups = new Map<string, CreationAddress[]>();

  for (const addr of addresses) {
    const streetName = addr.streetName || extractStreetName(addr.street);
    if (!streetGroups.has(streetName)) {
      streetGroups.set(streetName, []);
    }
    streetGroups.get(streetName)!.push(addr);
  }

  // Sort addresses within each street by house number
  for (const [, addrs] of streetGroups) {
    addrs.sort((a, b) => {
      const numA = parseInt(a.street.match(/^\d+/)?.[0] || '0', 10);
      const numB = parseInt(b.street.match(/^\d+/)?.[0] || '0', 10);
      return numA - numB;
    });
  }

  return streetGroups;
}

/**
 * Group addresses by polygon ID
 */
function groupByPolygon(addresses: CreationAddress[]): Map<string, CreationAddress[]> {
  const polygonGroups = new Map<string, CreationAddress[]>();

  for (const addr of addresses) {
    const polygonId = addr.polygonId || 'default';
    if (!polygonGroups.has(polygonId)) {
      polygonGroups.set(polygonId, []);
    }
    polygonGroups.get(polygonId)!.push(addr);
  }

  return polygonGroups;
}

/**
 * Find nearby streets that should be grouped together
 * Uses centroid distance to determine if streets are close enough
 */
function findNearbyStreets(
  streetGroups: Map<string, CreationAddress[]>,
  maxDistanceMeters: number = 200
): Map<string, string[]> {
  const streetCentroids = new Map<string, { lat: number; lng: number }>();

  // Calculate centroid for each street
  for (const [street, addrs] of streetGroups) {
    const centroid = {
      lat: addrs.reduce((sum, a) => sum + a.location.lat, 0) / addrs.length,
      lng: addrs.reduce((sum, a) => sum + a.location.lng, 0) / addrs.length,
    };
    streetCentroids.set(street, centroid);
  }

  // Find streets that are close to each other
  const nearbyGroups = new Map<string, string[]>();
  const streets = Array.from(streetCentroids.keys());

  for (let i = 0; i < streets.length; i++) {
    const street1 = streets[i];
    const centroid1 = streetCentroids.get(street1)!;
    const nearby: string[] = [street1];

    for (let j = i + 1; j < streets.length; j++) {
      const street2 = streets[j];
      const centroid2 = streetCentroids.get(street2)!;
      const distance = haversineDistance(
        centroid1.lat,
        centroid1.lng,
        centroid2.lat,
        centroid2.lng
      );

      if (distance <= maxDistanceMeters) {
        nearby.push(street2);
      }
    }

    if (nearby.length > 1) {
      nearbyGroups.set(street1, nearby);
    }
  }

  return nearbyGroups;
}

/**
 * Split addresses into workload groups based on options
 */
export function splitAddressesIntoWorkloads(
  addresses: CreationAddress[],
  options: SplitOptions
): WorkloadGroup[] {
  const { maxSize, splitByStreet, splitByPolygon, minSize = 10 } = options;
  const workloads: WorkloadGroup[] = [];
  let workloadIndex = 1;

  // If no splitting options, just split by size
  if (!splitByStreet && !splitByPolygon) {
    const sorted = sortAddressesForWalking(addresses);
    for (let i = 0; i < sorted.length; i += maxSize) {
      const chunk = sorted.slice(i, i + maxSize);
      const streets = [...new Set(chunk.map(a => a.streetName || extractStreetName(a.street)))];
      workloads.push({
        id: `workload_${workloadIndex}`,
        name: `Workload ${workloadIndex}`,
        addresses: chunk,
        metadata: {
          streets,
          bounds: calculateBounds(chunk),
        },
      });
      workloadIndex++;
    }
    return workloads;
  }

  // Group by polygon first if enabled
  const polygonGroups = splitByPolygon
    ? groupByPolygon(addresses)
    : new Map([['all', addresses]]);

  for (const [polygonId, polygonAddresses] of polygonGroups) {
    // Group by street if enabled
    if (splitByStreet) {
      const streetGroups = groupByStreet(polygonAddresses);
      const nearbyStreets = findNearbyStreets(streetGroups);

      // Track which streets have been assigned to workloads
      const assignedStreets = new Set<string>();

      // First pass: combine nearby streets into workloads
      for (const [primaryStreet, nearbyList] of nearbyStreets) {
        if (assignedStreets.has(primaryStreet)) continue;

        const groupAddresses: CreationAddress[] = [];
        const groupStreets: string[] = [];

        for (const street of nearbyList) {
          if (assignedStreets.has(street)) continue;

          const streetAddrs = streetGroups.get(street) || [];
          if (groupAddresses.length + streetAddrs.length <= maxSize) {
            groupAddresses.push(...streetAddrs);
            groupStreets.push(street);
            assignedStreets.add(street);
          }
        }

        if (groupAddresses.length > 0) {
          const sorted = sortAddressesForWalking(groupAddresses);

          // If still over maxSize, split further
          if (sorted.length > maxSize) {
            for (let i = 0; i < sorted.length; i += maxSize) {
              const chunk = sorted.slice(i, i + maxSize);
              workloads.push({
                id: `workload_${workloadIndex}`,
                name: groupStreets.length === 1
                  ? groupStreets[0]
                  : `${groupStreets[0]} Area ${Math.floor(i / maxSize) + 1}`,
                addresses: chunk,
                metadata: {
                  streets: groupStreets,
                  polygonId: polygonId !== 'all' ? polygonId : undefined,
                  bounds: calculateBounds(chunk),
                },
              });
              workloadIndex++;
            }
          } else {
            workloads.push({
              id: `workload_${workloadIndex}`,
              name: groupStreets.length === 1
                ? groupStreets[0]
                : `${groupStreets[0]} & nearby`,
              addresses: sorted,
              metadata: {
                streets: groupStreets,
                polygonId: polygonId !== 'all' ? polygonId : undefined,
                bounds: calculateBounds(sorted),
              },
            });
            workloadIndex++;
          }
        }
      }

      // Second pass: handle remaining single streets
      for (const [street, streetAddrs] of streetGroups) {
        if (assignedStreets.has(street)) continue;

        const sorted = sortAddressesForWalking(streetAddrs);

        // Split large streets into multiple workloads
        if (sorted.length > maxSize) {
          for (let i = 0; i < sorted.length; i += maxSize) {
            const chunk = sorted.slice(i, i + maxSize);
            workloads.push({
              id: `workload_${workloadIndex}`,
              name: `${street} (Part ${Math.floor(i / maxSize) + 1})`,
              addresses: chunk,
              metadata: {
                streets: [street],
                polygonId: polygonId !== 'all' ? polygonId : undefined,
                bounds: calculateBounds(chunk),
              },
            });
            workloadIndex++;
          }
        } else {
          workloads.push({
            id: `workload_${workloadIndex}`,
            name: street,
            addresses: sorted,
            metadata: {
              streets: [street],
              polygonId: polygonId !== 'all' ? polygonId : undefined,
              bounds: calculateBounds(sorted),
            },
          });
          workloadIndex++;
        }
      }
    } else {
      // Just split by polygon and size
      const sorted = sortAddressesForWalking(polygonAddresses);
      for (let i = 0; i < sorted.length; i += maxSize) {
        const chunk = sorted.slice(i, i + maxSize);
        const streets = [...new Set(chunk.map(a => a.streetName || extractStreetName(a.street)))];
        workloads.push({
          id: `workload_${workloadIndex}`,
          name: `Area ${workloadIndex}`,
          addresses: chunk,
          metadata: {
            streets,
            polygonId: polygonId !== 'all' ? polygonId : undefined,
            bounds: calculateBounds(chunk),
          },
        });
        workloadIndex++;
      }
    }
  }

  // Merge small workloads if they're geographically close
  const mergedWorkloads: WorkloadGroup[] = [];
  const toMerge: WorkloadGroup[] = [];

  for (const workload of workloads) {
    if (workload.addresses.length < minSize) {
      toMerge.push(workload);
    } else {
      mergedWorkloads.push(workload);
    }
  }

  // Try to merge small workloads with nearby ones
  for (const smallWorkload of toMerge) {
    let merged = false;
    const smallBounds = smallWorkload.metadata.bounds;

    if (smallBounds) {
      const smallCenter = {
        lat: (smallBounds.north + smallBounds.south) / 2,
        lng: (smallBounds.east + smallBounds.west) / 2,
      };

      // Find the closest workload that can absorb this one
      let closestIndex = -1;
      let closestDistance = Infinity;

      for (let i = 0; i < mergedWorkloads.length; i++) {
        const existing = mergedWorkloads[i];
        if (existing.addresses.length + smallWorkload.addresses.length > maxSize) continue;

        const existingBounds = existing.metadata.bounds;
        if (!existingBounds) continue;

        const existingCenter = {
          lat: (existingBounds.north + existingBounds.south) / 2,
          lng: (existingBounds.east + existingBounds.west) / 2,
        };

        const distance = haversineDistance(
          smallCenter.lat,
          smallCenter.lng,
          existingCenter.lat,
          existingCenter.lng
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      }

      if (closestIndex >= 0 && closestDistance < 300) {
        // Merge into the closest workload
        const target = mergedWorkloads[closestIndex];
        target.addresses.push(...smallWorkload.addresses);
        target.addresses = sortAddressesForWalking(target.addresses);
        target.metadata.streets = [
          ...new Set([...target.metadata.streets, ...smallWorkload.metadata.streets]),
        ];
        target.metadata.bounds = calculateBounds(target.addresses);
        merged = true;
      }
    }

    if (!merged) {
      // Keep as separate workload if can't merge
      mergedWorkloads.push(smallWorkload);
    }
  }

  // Renumber and return
  return mergedWorkloads.map((w, i) => ({
    ...w,
    id: `workload_${i + 1}`,
  }));
}

/**
 * Preview how addresses will be split into workloads
 */
export function previewWorkloadSplit(
  addresses: CreationAddress[],
  options: SplitOptions
): {
  workloadCount: number;
  workloads: Array<{
    name: string;
    addressCount: number;
    streets: string[];
  }>;
} {
  const groups = splitAddressesIntoWorkloads(addresses, options);

  return {
    workloadCount: groups.length,
    workloads: groups.map((g) => ({
      name: g.name,
      addressCount: g.addresses.length,
      streets: g.metadata.streets.slice(0, 5), // Limit to first 5 streets
    })),
  };
}

/**
 * Get unique street names from addresses
 */
export function getUniqueStreets(addresses: CreationAddress[]): string[] {
  const streets = new Set<string>();
  for (const addr of addresses) {
    const streetName = addr.streetName || extractStreetName(addr.street);
    if (streetName) {
      streets.add(streetName);
    }
  }
  return Array.from(streets).sort();
}

export type { SplitOptions, WorkloadGroup };
